import React, { memo, useMemo } from 'react';
import { Blok } from '../../interfaces/Blok.interface';
import { storyblokEditable } from 'gatsby-source-storyblok';

interface TooltipBlokProps extends Blok {
  id: string;
  text: string;
}

interface TooltipParagraphBlokProps extends Blok {
  copy: string;
  tooltips: TooltipBlokProps[];
}

interface TooltipParagraphProps {
  blok: TooltipParagraphBlokProps;
}

interface TooltipParagraphSection {
  text: string;
  tooltipText?: string;
}

const getTooltipParagraphRegularSection = (
  word: string
): TooltipParagraphSection => {
  return {
    text: word,
    tooltipText: undefined,
  };
};

const TooltipParagraph: React.FC<TooltipParagraphProps> = ({ blok }) => {
  const { copy, tooltips } = blok;

  const copySections: TooltipParagraphSection[] = useMemo(() => {
    return copy.split('<tooltip').map((chunk: string) => {
      if (chunk.includes('>')) {
        const tooltipChunkIdAndWord: string[] = chunk.split('>');
        const tooltipChunkWord: string = tooltipChunkIdAndWord[1];
        const tooltipChunkId: string = tooltipChunkIdAndWord[0]
          .replaceAll('"', '')
          .replaceAll('id=', '')
          .replaceAll(' ', '');
        const matchingTooltip: TooltipBlokProps | undefined = tooltips.find(
          (tooltip: TooltipBlokProps) => tooltip.id === tooltipChunkId
        );
        if (matchingTooltip) {
          return {
            text: tooltipChunkWord,
            tooltipText: matchingTooltip.text,
          };
        } else {
          const chunkWithoutInevitableTrailingArrow: string = chunk.replaceAll(
            '>',
            ''
          );
          return getTooltipParagraphRegularSection(
            chunkWithoutInevitableTrailingArrow
          );
        }
      } else {
        return getTooltipParagraphRegularSection(chunk);
      }
    });
  }, [copy, tooltips]);

  return (
    <div {...storyblokEditable(blok)} className="w-full h-auto overflow-x-clip">
      <p>
        {copySections.map((copySection: TooltipParagraphSection) => (
          <>
            {copySection.tooltipText && (
              <span className="not-prose relative group text-text/body-accent">
                <strong>
                  {copySection.text}
                  <sup>?</sup>
                </strong>
                <div className="hidden absolute top-auto w-[calc(100vw-32px)] bg-[#0B212E] text-system/white z-[1] p-2 md:left-[50%] md:-translate-x-[50%] md:w-full md:min-w-[300px] group-hover:block">
                  {copySection.tooltipText}
                </div>
              </span>
            )}
            {!copySection.tooltipText && copySection.text}
          </>
        ))}
      </p>
    </div>
  );
};

export default memo(TooltipParagraph);
